import { useState, useEffect } from 'preact/hooks';
import { api, type ChurchOverview } from '../services/api';
import { LoadingCard } from '../components/LoadingSpinner';
import { InviteLinksSection } from '../components/InviteLinksSection';
import { authState } from '../App';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

function fmtTime(t: string) {
  try {
    return new Date(`1970-01-01T${t}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return t;
  }
}

// ── Setup Banner ──────────────────────────────────────────────────────
function SetupBanner({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      style={{
        background: 'rgba(255, 246, 222, 0.8)',
        border: '1px solid rgba(244, 182, 64, 0.4)',
        borderRadius: 18,
        padding: '18px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div
          style={{
            width: 38,
            height: 38,
            background: 'var(--marigold-soft)',
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: '#7D5507',
          }}
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div>
          <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--ink)', margin: 0 }}>
            Configure Church Services
          </h4>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--ink-soft)', margin: '2px 0 0' }}>
            Add service times in your profile so families can check in.
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <a
          href="/profile"
          className="btn-secondary"
          style={{ padding: '6px 16px', fontSize: 12, textDecoration: 'none' }}
        >
          Add Services →
        </a>
        <button
          onClick={onDismiss}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--ink-faint)',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── Quick Actions ─────────────────────────────────────────────────────
const quickActions = [
  {
    href: '/checkins',
    eyebrow: 'Start session',
    title: 'Open Check-in Desk',
    bg: 'var(--paper)',
    iconBg: 'var(--marigold-soft)',
    iconColor: '#7D5507',
    path: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  },
  {
    href: '/families',
    eyebrow: 'Register',
    title: 'Add Family / Child',
    bg: 'var(--paper)',
    iconBg: 'var(--leaf-soft)',
    iconColor: '#2F7A4E',
    path: 'M12 4v16m8-8H4',
  },
  {
    href: '/team',
    eyebrow: 'Invite',
    title: 'Add Team Member',
    bg: 'var(--paper)',
    iconBg: 'var(--sky-soft)',
    iconColor: '#4E8EA8',
    path: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
  },
];

function QuickActions() {
  return (
    <div
      style={{
        background: 'var(--paper)',
        border: '1px solid rgba(25, 38, 49, 0.08)',
        borderRadius: 20,
        padding: '24px 26px',
        boxShadow: '0 2px 8px rgba(25, 38, 49, 0.02)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <p className="mini-label" style={{ margin: 0 }}>Wayfinding</p>
          <h2 className="section-title" style={{ margin: '2px 0 0' }}>Quick Actions</h2>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        {quickActions.map((a) => (
          <a
            key={a.href}
            href={a.href}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '14px 18px',
              background: a.bg,
              border: '1px solid rgba(25, 38, 49, 0.08)',
              borderRadius: 16,
              textDecoration: 'none',
              transition: 'all 160ms var(--ease-out)',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(25, 38, 49, 0.18)';
              (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 18px rgba(25, 38, 49, 0.04)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.transform = '';
              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(25, 38, 49, 0.08)';
              (e.currentTarget as HTMLElement).style.boxShadow = '';
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                background: a.iconBg,
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: a.iconColor,
              }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d={a.path} />
              </svg>
            </div>
            <div>
              <p
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-faint)',
                  margin: 0,
                }}
              >
                {a.eyebrow}
              </p>
              <p
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--ink)',
                  margin: '2px 0 0',
                }}
              >
                {a.title}
              </p>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────
export function Dashboard() {
  const [overview, setOverview] = useState<ChurchOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const { user, church } = authState.value;

  useEffect(() => {
    api
      .getChurchOverview()
      .then((data) => {
        if (data.success) {
          setOverview(data.overview);
          if (!data.overview?.services?.length) setShowBanner(true);
        } else {
          setError('Failed to load overview');
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setIsLoading(false));
  }, []);

  const isAdmin =
    (user as any)?.isAdmin ??
    (user?.role === 'admin' || (user as any)?.isInitialAdmin || (user as any)?.canInviteUsers);
  const userActualName =
    user?.firstName ||
    (user?.name && user.name !== 'Admin' ? user.name.split(' ')[0] : '') ||
    (user as any)?.first_name ||
    user?.email?.split('@')[0] ||
    'there';

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: '36px 24px 60px', display: 'flex', flexDirection: 'column', gap: 28 }}>
      
      {/* ── Ambient Greeting Area (Soft peach/pink glow per style.md) ── */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 24,
          padding: '36px 36px',
          background: 'radial-gradient(120% 140% at 85% -10%, rgba(254, 215, 170, 0.45) 0%, rgba(253, 230, 240, 0.35) 40%, rgba(255, 253, 248, 0.95) 85%)',
          border: '1px solid rgba(25, 38, 49, 0.07)',
          boxShadow: '0 4px 20px -2px rgba(25, 38, 49, 0.03)',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ maxWidth: 640 }}>
            <p
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--ink-faint)',
                margin: '0 0 6px',
              }}
            >
              {new Date().toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
            <h1
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 32,
                fontWeight: 600,
                letterSpacing: '-0.04em',
                color: 'var(--ink)',
                margin: '0 0 6px',
                lineHeight: 1.2,
              }}
            >
              {greeting()}, <span style={{ fontStyle: 'italic', fontWeight: 500 }}>{userActualName}</span>
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--ink-soft)', margin: 0, lineHeight: 1.5 }}>
              Here is what's happening at{' '}
              <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>{church?.name || 'your church'}</strong> today.
            </p>
          </div>

          {/* Live Indicator Badge */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 14px',
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(123, 166, 139, 0.3)',
              borderRadius: 999,
            }}
          >
            <span className="signal-dot signal-dot--live" />
            <span
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#2F7A4E',
              }}
            >
              Live Session
            </span>
          </div>
        </div>
      </div>

      {/* Setup banner if no services */}
      {showBanner && <SetupBanner onDismiss={() => setShowBanner(false)} />}

      {/* Error alert */}
      {error && (
        <div className="alert-error" style={{ borderRadius: 14 }}>
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20" style={{ flexShrink: 0 }}>
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div>
            <p style={{ fontWeight: 600, margin: 0 }}>Failed to load overview</p>
            <p style={{ fontSize: 13, opacity: 0.8, margin: '2px 0 0' }}>{error}</p>
          </div>
        </div>
      )}

      {/* Loading Skeleton */}
      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            <LoadingCard /><LoadingCard /><LoadingCard />
          </div>
          <LoadingCard />
        </div>
      )}

      {overview && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }} className="animate-slide-up">
          
          {/* ── Soft Minimalism Stats Row (Total Families, Total Children, Services) ── */}
          {/* Note: Attendance (Events recorded) was removed per design brief */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            
            {/* Total Families */}
            <div
              style={{
                background: 'var(--paper)',
                border: '1px solid rgba(25, 38, 49, 0.08)',
                borderRadius: 20,
                padding: '22px 24px',
                boxShadow: '0 2px 8px rgba(25, 38, 49, 0.02)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <p
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'var(--ink-faint)',
                    margin: 0,
                  }}
                >
                  Families
                </p>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: 'var(--marigold-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#7D5507',
                  }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 34,
                    fontWeight: 700,
                    letterSpacing: '-0.04em',
                    color: 'var(--ink)',
                    lineHeight: 1,
                  }}
                >
                  {overview.totalFamilies}
                </span>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--ink-soft)' }}>
                  families registered
                </span>
              </div>
            </div>

            {/* Total Children */}
            <div
              style={{
                background: 'var(--paper)',
                border: '1px solid rgba(25, 38, 49, 0.08)',
                borderRadius: 20,
                padding: '22px 24px',
                boxShadow: '0 2px 8px rgba(25, 38, 49, 0.02)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <p
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'var(--ink-faint)',
                    margin: 0,
                  }}
                >
                  Children
                </p>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: 'var(--leaf-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#2F7A4E',
                  }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 34,
                    fontWeight: 700,
                    letterSpacing: '-0.04em',
                    color: 'var(--ink)',
                    lineHeight: 1,
                  }}
                >
                  {overview.totalChildren}
                </span>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--ink-soft)' }}>
                  enrolled members
                </span>
              </div>
            </div>

            {/* Services */}
            <div
              style={{
                background: 'var(--paper)',
                border: '1px solid rgba(25, 38, 49, 0.08)',
                borderRadius: 20,
                padding: '22px 24px',
                boxShadow: '0 2px 8px rgba(25, 38, 49, 0.02)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <p
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'var(--ink-faint)',
                    margin: 0,
                  }}
                >
                  Services
                </p>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: 'var(--sky-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#4E8EA8',
                  }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 34,
                    fontWeight: 700,
                    letterSpacing: '-0.04em',
                    color: 'var(--ink)',
                    lineHeight: 1,
                  }}
                >
                  {overview.services?.length ?? 0}
                </span>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--ink-soft)' }}>
                  configured service times
                </span>
              </div>
            </div>

          </div>

          {/* ── Quick Actions ── */}
          <QuickActions />

          {/* ── Invite Links (Admin only) ── */}
          {isAdmin && <InviteLinksSection />}

          {/* ── Weekly Service Schedule (Full width, soft minimalism) ── */}
          {/* Note: "Recent Families" (Registered) and "Attendance History" (Historical) removed per prompt */}
          <div
            style={{
              background: 'var(--paper)',
              border: '1px solid rgba(25, 38, 49, 0.08)',
              borderRadius: 20,
              padding: '24px 28px',
              boxShadow: '0 2px 8px rgba(25, 38, 49, 0.02)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <p className="mini-label" style={{ margin: 0 }}>Weekly</p>
                <h2 className="section-title" style={{ margin: '2px 0 0' }}>Service Schedule</h2>
              </div>
              <span className="badge-marigold">
                {overview.services.length} {overview.services.length === 1 ? 'service' : 'services'}
              </span>
            </div>

            {overview.services.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '36px 0', color: 'var(--ink-soft)' }}>
                <p style={{ fontSize: 14, margin: 0 }}>No service times configured yet.</p>
                <a
                  href="/profile"
                  style={{
                    display: 'inline-block',
                    marginTop: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--marigold)',
                    textDecoration: 'none',
                  }}
                >
                  Add service times →
                </a>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
                {overview.services.map((s: any, i: number) => (
                  <div
                    key={s.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 14,
                      padding: '16px 18px',
                      background: 'rgba(25, 38, 49, 0.02)',
                      border: '1px solid rgba(25, 38, 49, 0.06)',
                      borderRadius: 16,
                      transition: 'all 160ms var(--ease-out)',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(255, 246, 222, 0.4)';
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(244, 182, 64, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(25, 38, 49, 0.02)';
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(25, 38, 49, 0.06)';
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 14,
                        fontWeight: 700,
                        color: 'var(--coral)',
                        minWidth: 22,
                        lineHeight: 1.4,
                      }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: 16,
                          fontWeight: 600,
                          letterSpacing: '-0.03em',
                          color: 'var(--ink)',
                          margin: 0,
                        }}
                      >
                        {s.name}
                      </p>
                      {s.description && (
                        <p style={{ fontSize: 12, color: 'var(--ink-soft)', margin: '3px 0 8px', lineHeight: 1.4 }}>
                          {s.description}
                        </p>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                        <span className="badge-marigold">{DAYS[s.weekday]}</span>
                        <span style={{ fontSize: 12, color: 'var(--ink-soft)', fontWeight: 500 }}>
                          {fmtTime(s.start_time)}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}>
                          · {s.duration_minutes} min
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
